import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";
import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import { PageContainer } from "../../components/styled/shared";
import { ACTION_COSTS, FREE_MONTHLY_CREDITS } from "../../constants/credits";

const MainContent = styled.div`
  flex: 1;
  padding: 120px 0 80px;

  @media (max-width: 767.98px) {
    padding: 100px 0 60px;
  }
`;

const PageTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
  margin-bottom: 24px;

  @media (max-width: 767.98px) {
    font-size: 36px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 20px;
  color: #6b7280;
  text-align: center;
  max-width: 700px;
  margin: 0 auto 60px;
  line-height: 1.6;
`;

const ContentSection = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 60px;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 767.98px) {
    padding: 40px 24px;
    border-radius: 12px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 24px;
  margin-top: 40px;

  &:first-child {
    margin-top: 0;
  }

  @media (max-width: 767.98px) {
    font-size: 28px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  color: #4a4a4a;
  line-height: 1.8;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin: 32px 0 24px;

  @media (max-width: 767.98px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 28px 24px;
`;

const FeatureTitle = styled.h3`
  font-size: 21px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 16px;
`;

const FeatureList = styled.ul`
  padding-left: 20px;
  margin: 0;
`;

const FeatureItem = styled.li`
  font-size: 16px;
  color: #4a4a4a;
  line-height: 1.7;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CTASection = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 40px;
  margin-top: 48px;
  text-align: center;

  @media (max-width: 767.98px) {
    padding: 32px 24px;
  }
`;

const CTATitle = styled.h2`
  font-size: 30px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 16px;

  @media (max-width: 767.98px) {
    font-size: 26px;
  }
`;

const CTAText = styled.p`
  font-size: 17px;
  color: #4a4a4a;
  line-height: 1.7;
  max-width: 650px;
  margin: 0 auto 24px;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: var(--brand-primary, #0e7e7a);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 28px;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.15s ease, transform 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--brand-primary-600, #0b6b68);
    transform: translateY(-1px);
    color: #fff;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 4px rgba(14, 126, 122, 0.12);
  }
`;

const NewAboutPage = () => {
  return (
    <PageContainer>
      <NewNavbar />

      <MainContent>
        <Container>
          <PageTitle>Your AI listing assistant</PageTitle>
          <PageSubtitle>
            Listino handles the research, pricing analysis, seller materials, and presentation work
            behind every listing, so you can spend more time advising clients and winning business.
          </PageSubtitle>

          <ContentSection>
            <SectionTitle>Why Listino exists</SectionTitle>
            <Paragraph>
              Every listing begins with the same work. Researching the property, analyzing
              comparable sales, developing a pricing strategy, organizing photos, and preparing
              seller materials all take time. Most agents do that work themselves, often between
              appointments or late at night.
            </Paragraph>

            <Paragraph>
              Listino was built to become another set of hands for that process. Start with an
              address and Listino prepares the research, recommendations, pricing guidance, seller
              materials, and presentation so more time can be spent with clients instead of
              assembling documents.
            </Paragraph>

            <Paragraph>
              Listino doesn't replace an agent's expertise. It simply makes that expertise faster to
              deliver.
            </Paragraph>

            <SectionTitle>More than a CMA</SectionTitle>

            <Paragraph>
              A comparative market analysis answers one important question: What is this home worth?
            </Paragraph>

            <Paragraph>
              Preparing a property for market requires much more. Agents need to decide the
              property's strengths, the surrounding market, likely buyers, pricing strategy,
              recommended improvements, photo direction, and how to communicate the plan clearly to
              sellers.
            </Paragraph>

            <Paragraph>
              Listino brings those pieces together into one complete listing package so agents can
              focus less on assembling materials and more on guiding their clients.
            </Paragraph>

            <SectionTitle>What&apos;s included</SectionTitle>

            <Paragraph>
              Every listing package brings together the research, strategy, and presentation work
              needed to prepare for the appointment and communicate a clear plan to the seller.
            </Paragraph>

            <FeatureGrid>
              <FeatureCard>
                <FeatureTitle>Research</FeatureTitle>

                <FeatureList>
                  <FeatureItem>Property details and market analysis</FeatureItem>
                  <FeatureItem>Comparable listings and a Quick CMA</FeatureItem>
                  <FeatureItem>Buyer insights and neighborhood context</FeatureItem>
                </FeatureList>
              </FeatureCard>

              <FeatureCard>
                <FeatureTitle>Strategy</FeatureTitle>

                <FeatureList>
                  <FeatureItem>Pricing guidance</FeatureItem>
                  <FeatureItem>Staging and photo recommendations</FeatureItem>
                  <FeatureItem>Property specific improvement suggestions</FeatureItem>
                </FeatureList>
              </FeatureCard>

              <FeatureCard>
                <FeatureTitle>Presentation</FeatureTitle>

                <FeatureList>
                  <FeatureItem>Personalized seller materials</FeatureItem>
                  <FeatureItem>PDF, PowerPoint, and one page exports</FeatureItem>
                  <FeatureItem>Social ready listing snapshots</FeatureItem>
                </FeatureList>
              </FeatureCard>
            </FeatureGrid>

            <Paragraph>
              Everything is editable, making it easy to personalize each package while keeping the
              agent&apos;s expertise, recommendations, and brand at the center.
            </Paragraph>

            <SectionTitle>How our analysis works</SectionTitle>
            <Paragraph>
              Listino combines property details, comparable sales, market data, and established real
              estate research to generate recommendations for each listing. Factual information is
              distinguished from AI generated recommendations, and data sources are identified
              whenever possible.
            </Paragraph>

            <Paragraph>
              More information about the data, research, and methodology behind the analysis is
              available on the{" "}
              <Link to="/resources/methodology" style={{ color: "#6b7280", fontWeight: 600 }}>
                methodology page
              </Link>
              .
            </Paragraph>

            <SectionTitle>How Listino is priced</SectionTitle>
            <Paragraph>
              Listino is priced in credits, and every account includes{" "}
              {FREE_MONTHLY_CREDITS.toLocaleString("en-US")} credits a month at no charge, with no
              card required. Credits pay for what Listino prepares: a Listing Analysis is{" "}
              {ACTION_COSTS.analysis} credits, and a branded PDF, Seller Presentation, one-pager,
              social set, photo touch-ups, or video can be added to it for a little more. Everything
              Listino prepares is available on every plan. Editing, branding, comps curation,
              sharing, and publishing never use credits, and if something fails to prepare, the
              credits come back automatically. Plans and credit packs are there for agents who
              prepare more than the free month covers.
            </Paragraph>

            <SectionTitle>Built with working agents</SectionTitle>
            <Paragraph>
              Listino was shaped by feedback from working real estate agents. Every workflow seeks
              to reflect how listings are actually prepared, from the first consultation through
              launching the property on the market. Have a suggestion? Email us at{" "}
              <a href="mailto:contact@listino.ai" style={{ color: "#6b7280", fontWeight: 600 }}>
                contact@listino.ai
              </a>
              .
            </Paragraph>

            <CTASection>
              <CTATitle>Ready to prepare your next listing?</CTATitle>

              <CTAText>
                Start with research and pricing guidance, then add seller materials, presentations,
                and social content as needed.
              </CTAText>

              <CTAButton to="/verify">Prepare a listing</CTAButton>
            </CTASection>
          </ContentSection>
        </Container>
      </MainContent>

      <NewFooter />
    </PageContainer>
  );
};

export default NewAboutPage;
